import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useDepartmentData from "layouts/tables/data/departmentTable";

function Department() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDepartment,
    setSelectedDepartment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDepartment,
  } = useDepartmentData();

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading departments...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      Church Departments
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDButton variant="gradient" color="dark" onClick={handleAddDepartment}>
                        <Icon>add</Icon>&nbsp;New Department
                      </MDButton>
                      <MDButton variant="gradient" color="dark" onClick={handlePrint}>
                        <Icon>print</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search departments..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Department Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            {selectedDepartment?.id ? "Edit Department" : "Add New Department"}
          </MDTypography>
          <MDInput
            label="Department Name"
            value={selectedDepartment?.departmentName || ""}
            onChange={(e) =>
              setSelectedDepartment({
                ...selectedDepartment,
                departmentName: e.target.value,
              })
            }
            fullWidth
            margin="normal"
          />
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setEditModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="info"
              onClick={handleEditSubmit}
            >
              {selectedDepartment?.id ? "Save Changes" : "Create Department"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      {/* Delete Department Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            Confirm Delete
          </MDTypography>
          <MDTypography>
            Are you sure you want to delete &quot;{selectedDepartment?.departmentName}&quot;
            department?
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setDeleteModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="error"
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

Department.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedDepartment: PropTypes.object,
  setSelectedDepartment: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddDepartment: PropTypes.func,
};

export default Department;
